/**
 * Outro Clip Configuration Component
 * 
 * Allows users to configure outro clip settings with design customization.
 */

import React from 'react';
import { OutroClipConfig, defaultOverlayConfig } from '../../types/overlayConfig';

interface OutroConfigProps {
  outroConfig: OutroClipConfig;
  onOutroChange: (config: OutroClipConfig) => void;
}

const OutroConfig: React.FC<OutroConfigProps> = React.memo(({
  outroConfig,
  onOutroChange,
}) => {
  const defaultOutro = defaultOverlayConfig.introOutro?.outro!;
  
  // Temporarily disable gradient settings (visible but non-interactable)
  const isGradientDisabled = true;

  const gradientTypeOptions: Array<{ value: OutroClipConfig['gradient']['type']; label: string }> = [
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'vertical', label: 'Vertical' },
    { value: 'radial', label: 'Radial' },
  ];

  // Shared gradient configuration component
  const GradientConfig: React.FC<{
    gradient: OutroClipConfig['gradient'];
    backgroundColor: string;
    textColor: string;
    onChange: (gradient: OutroClipConfig['gradient']) => void;
    isDisabled?: boolean;
  }> = ({ gradient, backgroundColor, textColor, onChange, isDisabled = false }) => (
    <div className={`space-y-2 ${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="enable-gradient-outro"
          checked={gradient?.enabled ?? false}
          disabled={isDisabled}
          onChange={(e) => !isDisabled && onChange({
            ...gradient,
            enabled: e.target.checked,
            type: gradient?.type || 'horizontal',
            direction: gradient?.direction ?? 0,
            startColor: gradient?.startColor || backgroundColor,
            endColor: gradient?.endColor || textColor,
            stops: gradient?.stops || [50, 50],
          })}
          className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        <label htmlFor="enable-gradient-outro" className={`text-xs font-medium ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}>
          Enable Gradient
        </label>
      </div>

      {gradient?.enabled && (
        <div className="space-y-2 pl-6">
          {/* Gradient Type */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
              Gradient Type
            </label>
            <select
              value={gradient.type || 'horizontal'}
              disabled={isDisabled}
              onChange={(e) => !isDisabled && onChange({
                ...gradient!,
                type: e.target.value as 'horizontal' | 'vertical' | 'radial',
              })}
              className={`w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
            >
              {gradientTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Direction (for horizontal/vertical) */}
          {gradient.type !== 'radial' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Direction: {gradient.direction ?? 0}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                step="1"
                value={gradient.direction ?? 0}
                onChange={(e) => onChange({
                  ...gradient!,
                  direction: parseInt(e.target.value),
                })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          )}

          {/* Radial Center Position */}
          {gradient.type === 'radial' && (
            <div className="space-y-2">
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  Center X (%)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={gradient.stops?.[0] ?? 50}
                    disabled={isDisabled}
                    onChange={(e) => !isDisabled && onChange({
                      ...gradient!,
                      stops: [parseInt(e.target.value), gradient.stops?.[1] ?? 50],
                    })}
                    className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                  <span className="text-xs text-gray-600 w-10 text-right">
                    {gradient.stops?.[0] ?? 50}%
                  </span>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  Center Y (%)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={gradient.stops?.[1] ?? 50}
                    disabled={isDisabled}
                    onChange={(e) => !isDisabled && onChange({
                      ...gradient!,
                      stops: [gradient.stops?.[0] ?? 50, parseInt(e.target.value)],
                    })}
                    className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                  <span className="text-xs text-gray-600 w-10 text-right">
                    {gradient.stops?.[1] ?? 50}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Start Color */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
              Start Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={gradient.startColor || backgroundColor}
                disabled={isDisabled}
                onChange={(e) => !isDisabled && onChange({
                  ...gradient!,
                  startColor: e.target.value,
                })}
                className={`w-8 h-8 border border-gray-300 rounded ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              />
              <input
                type="text"
                value={gradient.startColor || backgroundColor}
                disabled={isDisabled}
                onChange={(e) => !isDisabled && onChange({
                  ...gradient!,
                  startColor: e.target.value,
                })}
                className={`flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                placeholder="#000000"
              />
            </div>
          </div>

          {/* End Color */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isGradientDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
              End Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={gradient.endColor || textColor}
                disabled={isGradientDisabled}
                onChange={(e) => !isGradientDisabled && onChange({
                  ...gradient!,
                  endColor: e.target.value,
                })}
                className={`w-8 h-8 border border-gray-300 rounded ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              />
              <input
                type="text"
                value={gradient.endColor || textColor}
                disabled={isGradientDisabled}
                onChange={(e) => !isGradientDisabled && onChange({
                  ...gradient!,
                  endColor: e.target.value,
                })}
                className={`flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Outro handlers
  const handleOutroReset = () => {
    onOutroChange({
      ...outroConfig,
      textColor: defaultOutro.textColor,
      backgroundColor: defaultOutro.backgroundColor,
      opacity: defaultOutro.opacity,
      gradient: {
        enabled: defaultOutro.gradient.enabled,
        type: defaultOutro.gradient.type,
        direction: defaultOutro.gradient.direction,
        startColor: defaultOutro.gradient.startColor,
        endColor: defaultOutro.gradient.endColor,
        stops: defaultOutro.gradient.stops,
      },
    });
  };

  return (
    <div className="space-y-3">
      {/* Duration */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Duration: {outroConfig.duration}s
        </label>
        <input
          type="range"
          min="2"
          max="5"
          step="0.5"
          value={outroConfig.duration}
          onChange={(e) => onOutroChange({
            ...outroConfig,
            duration: parseFloat(e.target.value),
          })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>2s</span>
          <span>5s</span>
        </div>
      </div>

      {/* Design Settings - Colors */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-700">Colors</label>
          <button
            onClick={handleOutroReset}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Reset
          </button>
        </div>
        
        <div className="space-y-2">
          {/* Text Color */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Text Color
              <span className="ml-1 text-gray-400 cursor-help" title="Color of all text overlays (titles, messages, etc.)">(?)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={outroConfig.textColor}
                onChange={(e) => onOutroChange({
                  ...outroConfig,
                  textColor: e.target.value,
                  gradient: outroConfig.gradient ? {
                    ...outroConfig.gradient,
                    endColor: outroConfig.gradient.endColor === outroConfig.textColor 
                      ? e.target.value 
                      : outroConfig.gradient.endColor,
                  } : outroConfig.gradient,
                })}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={outroConfig.textColor}
                onChange={(e) => onOutroChange({
                  ...outroConfig,
                  textColor: e.target.value,
                  gradient: outroConfig.gradient ? {
                    ...outroConfig.gradient,
                    endColor: outroConfig.gradient.endColor === outroConfig.textColor 
                      ? e.target.value 
                      : outroConfig.gradient.endColor,
                  } : outroConfig.gradient,
                })}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Opacity */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-700">Opacity</label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={outroConfig.opacity ?? 1.0}
            onChange={(e) => onOutroChange({
              ...outroConfig,
              opacity: parseFloat(e.target.value),
            })}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <span className="text-xs text-gray-600 w-10 text-right">
            {((outroConfig.opacity ?? 1.0) * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Gradient */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <GradientConfig
          gradient={outroConfig.gradient}
          backgroundColor={outroConfig.backgroundColor}
          textColor={outroConfig.textColor}
          isDisabled={isGradientDisabled}
          onChange={(gradient) => onOutroChange({
            ...outroConfig,
            gradient,
          })}
        />
      </div>

      {/* Background Color */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Background Color
          <span className="ml-1 text-gray-400 font-normal cursor-help" title="Base background color of the clip (used when gradient is disabled)">(?)</span>
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            value={outroConfig.backgroundColor}
            onChange={(e) => onOutroChange({
              ...outroConfig,
              backgroundColor: e.target.value,
              gradient: outroConfig.gradient ? {
                ...outroConfig.gradient,
                startColor: outroConfig.gradient.startColor === outroConfig.backgroundColor 
                  ? e.target.value 
                  : outroConfig.gradient.startColor,
              } : outroConfig.gradient,
            })}
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={outroConfig.backgroundColor}
            onChange={(e) => onOutroChange({
              ...outroConfig,
              backgroundColor: e.target.value,
              gradient: outroConfig.gradient ? {
                ...outroConfig.gradient,
                startColor: outroConfig.gradient.startColor === outroConfig.backgroundColor 
                  ? e.target.value 
                  : outroConfig.gradient.startColor,
              } : outroConfig.gradient,
            })}
            className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="#000000"
          />
        </div>
      </div>

      {/* Content Settings */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="show-tournament-logo-outro"
            checked={outroConfig.showTournamentLogo ?? true}
            onChange={(e) => onOutroChange({
              ...outroConfig,
              showTournamentLogo: e.target.checked,
            })}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="show-tournament-logo-outro" className="text-xs text-gray-700">
            Show tournament logo and name
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="show-thanks-message-outro"
            checked={outroConfig.showThanksMessage ?? true}
            onChange={(e) => onOutroChange({
              ...outroConfig,
              showThanksMessage: e.target.checked,
            })}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="show-thanks-message-outro" className="text-xs text-gray-700">
            Show thanks message
          </label>
        </div>

        {outroConfig.showThanksMessage && (
          <div className="pl-6">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Custom Message (optional)
            </label>
            <input
              type="text"
              value={outroConfig.customMessage || ''}
              onChange={(e) => onOutroChange({
                ...outroConfig,
                customMessage: e.target.value || undefined,
              })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Thanks for watching!"
            />
          </div>
        )}
      </div>
    </div>
  );
});

OutroConfig.displayName = 'OutroConfig';

export default OutroConfig;
